import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  effect,
  linkedSignal,
  signal,
} from '@angular/core';
import { form, pattern, required, submit } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { TranslateModule } from '@ngx-translate/core';
import { SignalFormServerErrors } from '@tourfold/libraries/shared-util';

interface WebhookEventType {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface WebhookEndpointWithSubscriptions {
  id: string;
  displayName?: string;
  endpointUrl: string;
  enabled: boolean;
  retry: boolean;
  subscriptionIds?: string[];
  invalidSubscriptionIds?: string[];
  lockVersion: number;
}

interface EventTypeOption {
  label: string;
  value: string;
  category: string;
}

interface WebhookFormData {
  name: string;
  url: string;
  enabled: boolean;
  retry: boolean;
  eventTypeIds: string[];
}

const EMPTY_FORM: WebhookFormData = {
  name: '',
  url: '',
  enabled: false,
  retry: false,
  eventTypeIds: [],
};

const WEBHOOK_FORM_FIELDS = ['name', 'url'] as const satisfies readonly (keyof WebhookFormData)[];

// Mirrors the backend's CreateWebhookEndpointRequest.WEBHOOK_URL_REGEX so the
// client and server enforce the same URL contract — no auto-prefix, must
// start with http:// or https://.
const WEBHOOK_URL_PATTERN = /^https?:\/\/.+$/i;

export type WebhookSavePayload = {
  name: string;
  url: string;
  enabled: boolean;
  retry: boolean;
  eventTypeIds: string[];
  lockVersion?: number;
};

export type WebhookSaveResult = { secret?: string } | void;

@Component({
  selector: 'tf-settings-webhook-dialog',
  imports: [
    FormsModule,
    CommonModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    MessageModule,
    TagModule,
    ToggleSwitchModule,
    TooltipModule,
    MultiSelectModule,
    TranslateModule,
  ],
  templateUrl: './webhook-dialog.html',
  styleUrl: './webhook-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebhookDialog {
  // Inputs
  readonly visible = input.required<boolean>();
  readonly endpoint = input<WebhookEndpointWithSubscriptions | null>(null);
  readonly eventTypes = input.required<WebhookEventType[]>();
  readonly eventTypeOptions = input.required<EventTypeOption[]>();
  /**
   * Promise-returning save callback. The dialog awaits it; on rejection it routes
   * RFC 9457 ProblemDetail errors into per-field messages and stays open so the
   * user keeps their input. The parent should re-throw HTTP errors so the dialog
   * can surface them. If the resolved value carries a `secret`, the dialog stays
   * open in a reveal step so the user can copy it once.
   */
  readonly onSave = input.required<(payload: WebhookSavePayload) => Promise<WebhookSaveResult>>();

  // Outputs
  readonly cancelled = output<void>();
  readonly visibilityChange = output<boolean>();
  readonly secretAcknowledged = output<void>();

  readonly formModel = linkedSignal<WebhookEndpointWithSubscriptions | null, WebhookFormData>({
    source: this.endpoint,
    computation: (endpoint): WebhookFormData => {
      if (!endpoint) return { ...EMPTY_FORM };
      const invalidIds = new Set(endpoint.invalidSubscriptionIds ?? []);
      const validSubscriptionIds = (endpoint.subscriptionIds ?? []).filter(
        (id) => !invalidIds.has(id)
      );
      return {
        name: endpoint.displayName ?? '',
        url: endpoint.endpointUrl,
        enabled: endpoint.enabled,
        retry: endpoint.retry ?? false,
        eventTypeIds: validSubscriptionIds,
      };
    },
  });

  readonly webhookForm = form(this.formModel, (schema) => {
    required(schema.name, { message: 'SETTINGS_WEBHOOKS.NAME_REQUIRED' });
    required(schema.url, { message: 'SETTINGS_WEBHOOKS.URL_REQUIRED' });
    pattern(schema.url, WEBHOOK_URL_PATTERN, { message: 'SETTINGS_WEBHOOKS.URL_INVALID' });
  });

  readonly serverErrors = new SignalFormServerErrors(this.formModel, WEBHOOK_FORM_FIELDS, {
    pointerToField: {
      displayName: 'name',
      endpointUrl: 'url',
    },
  });

  readonly selectedEventTypeIds = computed(() => this.formModel().eventTypeIds);

  readonly selectedEventTypesDetails = computed(() => {
    const selectedIds = this.selectedEventTypeIds();
    return this.eventTypes().filter((et) => selectedIds.includes(et.id));
  });

  readonly selectedEventTypesCount = computed(() => this.selectedEventTypeIds().length);

  readonly invalidSubscriptionsList = computed(() => {
    const endpointValue = this.endpoint();
    return endpointValue?.invalidSubscriptionIds ?? [];
  });

  readonly saveAttempted = signal(false);
  readonly isSaving = signal(false);
  readonly revealedSecret = signal<string | null>(null);
  readonly secretCopied = signal(false);
  readonly secretCopyFailed = signal(false);

  readonly canSave = computed(() => !this.webhookForm().invalid());

  readonly isRevealingSecret = computed(() => this.revealedSecret() !== null);

  /**
   * Whether to render the inline URL error: pattern (format) errors show
   * immediately as the user types, required (empty) errors wait for the first
   * Save attempt to avoid yelling at the user before they engage. Server errors
   * always win.
   */
  readonly showUrlError = computed(() => {
    if (this.serverErrors.get('url')) return true;
    if (!this.webhookForm.url().invalid()) return false;
    const firstErrorKind = this.webhookForm.url().errors()[0]?.kind;
    return firstErrorKind === 'pattern' || this.saveAttempted();
  });

  readonly isEditMode = computed(() => this.endpoint() !== null);

  readonly dialogTitle = computed(() =>
    this.isEditMode() ? 'SETTINGS_WEBHOOKS.EDIT_ENDPOINT' : 'SETTINGS_WEBHOOKS.ADD_ENDPOINT'
  );

  readonly inputIdPrefix = computed(() => (this.isEditMode() ? 'edit' : 'create'));

  constructor() {
    // Clear server errors and any prior secret-reveal state whenever the dialog
    // re-opens or the endpoint switches.
    effect(() => {
      this.endpoint();
      this.visible();
      this.serverErrors.clear();
      this.revealedSecret.set(null);
      this.secretCopied.set(false);
      this.secretCopyFailed.set(false);
    });
  }

  onNameChange(value: string): void {
    this.formModel.update((m) => ({ ...m, name: value }));
  }

  onUrlChange(value: string): void {
    this.formModel.update((m) => ({ ...m, url: value }));
  }

  onEnabledChange(value: boolean): void {
    this.formModel.update((m) => ({ ...m, enabled: value }));
  }

  onRetryChange(value: boolean): void {
    this.formModel.update((m) => ({ ...m, retry: value }));
  }

  onEventTypeIdsChange(value: string[]): void {
    this.formModel.update((m) => ({ ...m, eventTypeIds: value }));
  }

  async handleSave(): Promise<void> {
    if (this.isSaving()) return;
    this.saveAttempted.set(true);
    await submit(this.webhookForm, async () => {
      const formValue = this.formModel();
      const payload: WebhookSavePayload = {
        name: formValue.name,
        url: formValue.url,
        enabled: formValue.enabled,
        retry: formValue.retry,
        eventTypeIds: formValue.eventTypeIds,
        lockVersion: this.endpoint()?.lockVersion,
      };

      this.isSaving.set(true);
      this.serverErrors.clear();
      try {
        const result = await this.onSave()(payload);
        const secret = result?.secret;
        if (typeof secret === 'string' && secret.length > 0) {
          // Hold the dialog open in reveal mode — the parent waits for
          // secretAcknowledged before closing and reloading the list.
          this.revealedSecret.set(secret);
        }
        // Otherwise parent closes the dialog via [visible] / closeDialog().
      } catch (err) {
        await this.serverErrors.apply(err);
      } finally {
        this.isSaving.set(false);
      }
    });
  }

  async copySecret(): Promise<void> {
    const secret = this.revealedSecret();
    if (secret === null) return;
    try {
      await navigator.clipboard.writeText(secret);
      this.secretCopied.set(true);
      this.secretCopyFailed.set(false);
    } catch {
      this.secretCopied.set(false);
      this.secretCopyFailed.set(true);
    }
  }

  acknowledgeSecret(): void {
    this.revealedSecret.set(null);
    this.secretCopied.set(false);
    this.secretCopyFailed.set(false);
    this.saveAttempted.set(false);
    this.webhookForm().reset({ ...EMPTY_FORM });
    this.formModel.set({ ...EMPTY_FORM });
    this.secretAcknowledged.emit();
  }

  onCancel(): void {
    if (this.isSaving()) return;
    // While the secret is being revealed, the cancel/close affordance has the
    // same meaning as "I've saved it" — the secret cannot be retrieved later.
    if (this.isRevealingSecret()) {
      this.acknowledgeSecret();
      return;
    }
    this.saveAttempted.set(false);
    this.serverErrors.clear();
    this.webhookForm().reset({ ...EMPTY_FORM });
    this.formModel.set({ ...EMPTY_FORM });
    this.cancelled.emit();
  }

  onVisibleChange(visible: boolean): void {
    this.visibilityChange.emit(visible);
    if (!visible) {
      this.onCancel();
    }
  }
}
